import React, { useEffect } from 'react';
import {
  withTranslation,
  Button,
  Tooltip,
} from '@piwikpro/ui-components';
import { TFunction } from 'react-i18next';
import { AuthSettings } from '@piwikpro/auth-crate';
import { errorBoundary, withInject } from '@piwikpro/platform';

interface SecurityProps {
  t: TFunction
  authSettings: AuthSettings
  user: any
  loggedUserId: string
  has2faEnabled: boolean
  hasSamlEnabled: boolean
  isLoading: boolean
  failed: boolean
}

const Security: React.FunctionComponent<SecurityProps> = ({
  authSettings,
  user,
  has2faEnabled,
  hasSamlEnabled,
  isLoading,
  failed,
  loggedUserId,
  t,
}) => {
  const fetchData = async () => {
    try {
      await authSettings.getSettingsForUser(user.id);
      // eslint-disable-next-line no-empty
    } catch {}
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleUser2faReset = async () => {
    try {
      await authSettings.reset2faForUser(
        {
          id: user.id,
          successMessage: t('security.reset2fa.notification.success', { email: user.attributes.email }),
        },
      );
      fetchData();
      // eslint-disable-next-line no-empty
    } catch {}
  };

  if (hasSamlEnabled) {
    return null;
  }

  const isLoggedUser = user.id === loggedUserId;
  const isDisabled = isLoading || failed || isLoggedUser || !has2faEnabled;

  const getTooltipText = () => {
    if (isLoading) return false;
    if (failed) return 'accountSecurity.twoFactorAuth.load.failed.description';
    if (isLoggedUser) return 'users.security.reset2fa.reset.blockReason.loggedUser';
    if (!has2faEnabled) return 'users.security.reset2fa.reset.blockReason.2faNotConfigured';

    return false;
  };
  const tooltipText = getTooltipText();

  const button = (
    <Button
      appearance="default"
      disabled={isDisabled}
      onClick={handleUser2faReset}
      text={t('users.security.reset2fa.buttonText')}
    />
  );

  if (tooltipText) {
    return (
      <Tooltip content={t(tooltipText)}>
        {button}
      </Tooltip>
    );
  }

  return button;
};

export default withInject({
  authSettings: 'AuthCrate.authSettings',
})(withTranslation('administration')(errorBoundary(Security)));
